import {
  API_BASE_URL,
  WS_URL,
  SENTRY_DSN,
  SENTRY_ENABLED,
  SENTRY_ENVIRONMENT,
} from "./env";

type RuntimeConfig = {
  apiBaseUrl: string;
  wsUrl: string;
  // Sentry toggle — served per-deploy by nginx so ops can enable/disable
  // without an image rebuild. Falls back to build-time env for local dev.
  sentryDsn: string;
  sentryEnabled: boolean;
  sentryEnvironment: string;
};

const defaults: RuntimeConfig = {
  apiBaseUrl: API_BASE_URL,
  wsUrl: WS_URL,
  sentryDsn: SENTRY_DSN,
  sentryEnabled: SENTRY_ENABLED,
  sentryEnvironment: SENTRY_ENVIRONMENT,
};

let cachedConfig: RuntimeConfig | null = null;
let loadingPromise: Promise<RuntimeConfig> | null = null;

function normalizeApiBaseUrl(url: string): string {
  return url.endsWith("/") ? url.slice(0, -1) : url;
}

function normalizeConfig(config: Partial<RuntimeConfig>): RuntimeConfig {
  return {
    apiBaseUrl: normalizeApiBaseUrl(config.apiBaseUrl ?? defaults.apiBaseUrl),
    wsUrl: config.wsUrl ?? defaults.wsUrl,
    // Older runtime-config.json payloads omit these — fall back to defaults so
    // an un-migrated nginx image doesn't crash config parsing.
    sentryDsn: config.sentryDsn ?? defaults.sentryDsn,
    sentryEnabled: config.sentryEnabled ?? defaults.sentryEnabled,
    sentryEnvironment: config.sentryEnvironment ?? defaults.sentryEnvironment,
  };
}

export function getRuntimeConfigSync(): RuntimeConfig {
  return cachedConfig ?? normalizeConfig(defaults);
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cachedConfig) return cachedConfig;

  if (typeof window === "undefined") {
    cachedConfig = normalizeConfig(defaults);
    return cachedConfig;
  }

  if (loadingPromise) return loadingPromise;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 3000);

  loadingPromise = fetch("/runtime-config", {
    cache: "no-store",
    signal: controller.signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error("Failed to load runtime config");
      }

      const config = (await response.json()) as Partial<RuntimeConfig>;
      cachedConfig = normalizeConfig(config);
      return cachedConfig;
    })
    .catch(() => {
      cachedConfig = normalizeConfig(defaults);
      return cachedConfig;
    })
    .finally(() => {
      clearTimeout(timeoutId);
      loadingPromise = null;
    });

  return loadingPromise;
}
